/**
 * How a map line is stroked (decision 654, docs/proposals/2026-10-26-map-colours.md §5.2): the line itself and what is
 * drawn under it, bottom first. Every map engine (Leaflet in Teslyn Field and Teslyn Cloud) turns these into paths;
 * colours stay token names, resolved from the CSS variables by the engine, and sizes and opacities come from the
 * `map` tokens the engine reads (`StrokeSizes`).
 *
 * - The glow of a line ticked for the plan: `map-line-selected`, `map-glow-width` wide, at `map-glow-opacity` (white
 *   at 85 % over imagery, dark at 35 % on light maps), solid, under everything else.
 * - The thin dark edge (casing): `map-line-casing`, `map-casing-width` wider than the line, at `map-casing-opacity`,
 *   dashed with the line so a dashed line stays dashed (solid under a dash-dot hazard line, as the proposal drew it).
 * - The line: dashed lines `3w 2w`, dash-dot hazard lines `8-3-2-3` at 2.5 px (scaled with the width, square ends so
 *   the dot stays a dot), dotted outlines round dots `2.6w` apart.
 * - A faded line (the line states under a plan) is drawn at `map-fade-opacity`, its edge and glow with it.
 */
import type { FeatureStyle, MapColour } from './map-layers.js';

/** The sizes and opacities of the `map` tokens, as the engine reads them. */
export interface StrokeSizes {
  /** The line's width, px (`map-stroke-thin`, `map-stroke`, `map-stroke-thick`). */
  readonly weight: number;
  /** `map-casing-width`, px: how much wider the edge is than the line. */
  readonly casingWidth: number;
  readonly casingOpacity: number;
  /** `map-glow-width`, px. */
  readonly glowWidth: number;
  /** `map-glow-opacity`: by base map. */
  readonly glowOpacity: number;
  /** `map-fade-opacity`. */
  readonly fadeOpacity: number;
}

/** One path of a line: its colour, width, opacity and dash. */
export interface StrokeSpec {
  readonly role: 'glow' | 'casing' | 'line';
  readonly colour: FeatureStyle['colour'];
  readonly weight: number;
  readonly opacity: number;
  readonly dashArray: string | undefined;
  readonly lineCap: 'round' | 'butt';
}

const n = (x: number): string => String(Math.round(x * 100) / 100);

/** The dash of a line `weight` px wide, and its ends. */
export function dashOf(
  style: Pick<FeatureStyle, 'dashed' | 'pattern'>,
  weight: number,
): { dashArray: string | undefined; lineCap: 'round' | 'butt' } {
  if (style.pattern === 'dash-dot')
    return { dashArray: [3.2, 1.2, 0.8, 1.2].map((k) => n(k * weight)).join(' '), lineCap: 'butt' };
  if (style.pattern === 'dotted')
    return { dashArray: `${n(weight * 0.1)} ${n(weight * 2.6)}`, lineCap: 'round' };
  // dashes scale with the line so thin and thick lines read alike
  if (style.dashed) return { dashArray: `${n(weight * 3)} ${n(weight * 2)}`, lineCap: 'round' };
  return { dashArray: undefined, lineCap: 'round' };
}

/** The paths of a line, bottom first: its glow (ticked for the plan), its dark edge, then the line. */
export function strokeStack(style: FeatureStyle, sizes: StrokeSizes, opacity: number): StrokeSpec[] {
  const o = style.faded ? opacity * sizes.fadeOpacity : opacity;
  const dash = dashOf(style, sizes.weight);
  const out: StrokeSpec[] = [];
  if (style.glow)
    out.push({
      role: 'glow',
      colour: 'map-line-selected' satisfies MapColour,
      weight: sizes.glowWidth,
      opacity: o * sizes.glowOpacity,
      dashArray: undefined,
      lineCap: 'round',
    });
  if (style.casing)
    out.push({
      role: 'casing',
      colour: 'map-line-casing' satisfies MapColour,
      weight: sizes.weight + sizes.casingWidth,
      opacity: o * sizes.casingOpacity,
      // a dash-dot hazard line over a solid edge; a dashed line's edge dashed with it
      dashArray: style.pattern === 'dash-dot' ? undefined : dash.dashArray,
      lineCap: dash.lineCap,
    });
  out.push({ role: 'line', colour: style.colour, weight: sizes.weight, opacity: o, ...dash });
  return out;
}
